import React from 'react';
import { Helmet } from 'react-helmet-async';
import HeroSlider from '../components/home/HeroSlider';
import WelcomeSection from '../components/home/WelcomeSection';
import FeaturedProducts from '../components/home/FeaturedProducts';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplet, Shield, Sparkles, Star } from 'lucide-react';

const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>LuxuryStyle - Premium Waterproof & Tarnish-Free Jewellery</title>
        <meta 
          name="description" 
          content="Discover LuxuryStyle's collection of premium waterproof and tarnish-free jewellery. 18K gold plated stainless steel bracelets and earrings designed for everyday elegance." 
        />
        <meta property="og:title" content="LuxuryStyle - Premium Waterproof Jewellery" />
        <meta property="og:description" content="Discover premium waterproof and tarnish-free jewellery. 18K gold plated stainless steel designed for everyday elegance." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://luxurystyle.info" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://luxurystyle.info" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "LuxuryStyle",
            "url": "https://luxurystyle.info",
            "logo": "https://luxurystyle.info/logo.png",
            "description": "Premium waterproof and tarnish-free jewellery"
          })}
        </script>
      </Helmet>

      <main data-testid="home-page">
        {/* Hero Slider */}
        <HeroSlider />

        {/* Welcome Section */}
        <WelcomeSection />

        {/* Featured Products */}
        <FeaturedProducts />

        {/* Why Choose Us Section */}
        <section data-testid="why-choose-us" className="py-20 md:py-32 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
                Why LuxuryStyle
              </span>
              <h2 className="text-4xl md:text-5xl font-medium text-white mt-4 mb-4">
                The LuxuryStyle Difference
              </h2>
              <div className="gold-divider mb-6" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Waterproof */}
              <div className="section-card p-8 text-center group hover:-translate-y-1 transition-all duration-500">
                <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                  <Droplet className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium text-white mb-4">100% Waterproof</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Wear your jewellery anywhere - in the shower, pool, or ocean. 
                  Our pieces are designed to withstand water without losing their shine.
                </p>
              </div>

              {/* Tarnish-Free */}
              <div className="section-card p-8 text-center group hover:-translate-y-1 transition-all duration-500">
                <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                  <Shield className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium text-white mb-4">Tarnish-Free</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Say goodbye to green fingers and faded jewellery. Our stainless steel 
                  construction ensures lasting brilliance for years to come.
                </p>
              </div>

              {/* Premium Quality */}
              <div className="section-card p-8 text-center group hover:-translate-y-1 transition-all duration-500">
                <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                  <Sparkles className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium text-white mb-4">18K Gold Plated</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Premium 18K gold plating on high-quality stainless steel creates 
                  the perfect balance of luxury aesthetics and durability.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Google Review CTA Section */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0a0a0a] via-[#111] to-[#0a0a0a]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <div className="flex justify-center mb-4">
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-[#D4AF37] fill-[#D4AF37]" strokeWidth={1.5} />
                ))}
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-medium text-white mb-4">
              Love Your LuxuryStyle Jewellery?
            </h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">
              We'd love to hear about your experience! Share your thoughts and help others discover the elegance of waterproof luxury.
            </p>
            <a
              href="https://share.google/jNF6BvocJeA38Cdry"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-google-review"
              className="btn-luxury inline-flex items-center space-x-2"
            >
              <Star className="w-5 h-5 fill-current" strokeWidth={1.5} />
              <span>Leave a Google Review</span>
            </a>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 md:py-32 relative overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1737816473252-b7b8b60ae7b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBnb2xkJTIwamV3ZWxyeSUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc2ODgyOTQ4Nnww&ixlib=rb-4.1.0&q=85)'
            }}
          />
          <div className="absolute inset-0 bg-black/80" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6">
              Elevate Your Style
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
              Discover the perfect piece that speaks to your unique style. 
              Browse our complete collection and find your next favorite accessory.
            </p>
            <Link
              to="/jewellery"
              data-testid="cta-shop-now"
              className="btn-luxury inline-flex items-center space-x-2"
            >
              <span>Shop Now</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default HomePage;
